import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Client } from '../services/clientApi';
import {
  CreateMealRequest,
  DailyNutritionSummary,
  Meal,
  mealApi,
} from '../services/mealApi';
import { colors } from '../theme';
import CancelButton from './CancelButton';
import Loading from './Loading';
import MealEntryModal from './MealEntryModal';

interface NutritionTrackerProps {
  clientId: string;
  clientName: string;
  selectedDate: Date;
  isDietitian?: boolean; // Diyetisyen ise edit izni var (danışan sayfasında)
  /** Diyetisyen danışan detayda false: sadece görüntüleme, öğün ekleme/düzenleme/silme yok */
  canAddEditMeals?: boolean;
  client?: Client | null; // Hedef bilgileri için
}

const NutritionTracker: React.FC<NutritionTrackerProps> = ({
  clientId,
  clientName,
  selectedDate,
  isDietitian = false,
  canAddEditMeals = true,
  client = null,
}) => {
  const [summary, setSummary] = useState<DailyNutritionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMealModalOpen, setIsMealModalOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);

  const fetchDailySummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mealApi.getDailyNutritionSummary(
        clientId,
        selectedDate
      );
      setSummary(data);
    } catch (err) {
      setError('Beslenme verileri yüklenirken hata oluştu');
      console.error('Error fetching daily summary:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedDate]);

  useEffect(() => {
    fetchDailySummary();
  }, [fetchDailySummary]);

  const handleAddMeal = async (mealData: CreateMealRequest) => {
    try {
      setLoading(true);
      if (editingMeal) {
        // Güncelleme
        await mealApi.updateMeal(editingMeal.id, mealData);
      } else {
        // Yeni ekleme
        await mealApi.createMeal(mealData);
      }
      await fetchDailySummary();
    } catch (err) {
      setError(
        editingMeal
          ? 'Öğün güncellenirken hata oluştu'
          : 'Öğün eklenirken hata oluştu'
      );
      console.error('Error saving meal:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMeal = async (mealId: string) => {
    if (!window.confirm('Bu öğünü silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await mealApi.deleteMeal(mealId);
      await fetchDailySummary();
    } catch (err) {
      setError('Öğün silinirken hata oluştu');
      console.error('Error deleting meal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMealModal = (meal?: Meal) => {
    if (meal) {
      setEditingMeal(meal);
    } else {
      setEditingMeal(null);
    }
    setIsMealModalOpen(true);
  };

  const handleCloseMealModal = () => {
    setIsMealModalOpen(false);
    setEditingMeal(null);
  };

  const getMealTypeColor = (mealType: string) => {
    const colors: Record<string, string> = {
      Kahvaltı: '#FF9800',
      'Ara Öğün': '#9C27B0',
      'Öğle Yemeği': '#43A047',
      'Akşam Yemeği': '#A5D6A7',
      'Gece Atıştırması': '#607D8B',
    };
    return colors[mealType] || '#757575';
  };

  // Önerilen günlük değerler (örnek - gerçek uygulamada kullanıcıya göre ayarlanmalı)
  // Client'tan gelen günlük hedefler veya default değerler
  const recommendedCalories = client?.dailyCalories || 2000;
  const recommendedProtein = client?.dailyProtein || 150; // gram
  const recommendedCarbs = client?.dailyCarbs || 250; // gram
  const recommendedFat = client?.dailyFat || 65; // gram

  const getProgressPercentage = (current: number, recommended: number) => {
    return Math.min((current / recommended) * 100, 100);
  };

  return loading && !summary ? (
    <Loading
      size="large"
      variant="wave"
      mode="inline"
      message="Yemek takibi verileri yükleniyor..."
    />
  ) : (
    <Box>
      {error && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffebee' }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Özet Kartları */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Card elevation={2}>
          <CardContent>
            <Typography variant="body2" sx={{ color: '#607D8B' }} gutterBottom>
              Toplam Kalori
            </Typography>
            <Typography variant="h4" sx={{ color: '#2E7D32' }}>
              {summary?.totalCalories.toFixed(0) || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: '#607D8B' }}>
              / {recommendedCalories} kcal
            </Typography>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage(
                summary?.totalCalories || 0,
                recommendedCalories
              )}
              sx={{
                mt: 1,
                bgcolor: '#F9FAF7',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#43A047',
                },
              }}
            />
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent>
            <Typography variant="body2" sx={{ color: '#607D8B' }} gutterBottom>
              Protein
            </Typography>
            <Typography variant="h4" sx={{ color: '#43A047' }}>
              {summary?.totalProtein.toFixed(1) || 0}g
            </Typography>
            <Typography variant="caption" color="text.secondary">
              / {recommendedProtein}g
            </Typography>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage(
                summary?.totalProtein || 0,
                recommendedProtein
              )}
              sx={{ mt: 1 }}
              color="success"
            />
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Karbonhidrat
            </Typography>
            <Typography variant="h4" sx={{ color: '#FF9800' }}>
              {summary?.totalCarbohydrates.toFixed(1) || 0}g
            </Typography>
            <Typography variant="caption" color="text.secondary">
              / {recommendedCarbs}g
            </Typography>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage(
                summary?.totalCarbohydrates || 0,
                recommendedCarbs
              )}
              sx={{ mt: 1 }}
              color="warning"
            />
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Yağ
            </Typography>
            <Typography variant="h4" sx={{ color: '#9C27B0' }}>
              {summary?.totalFat.toFixed(1) || 0}g
            </Typography>
            <Typography variant="caption" color="text.secondary">
              / {recommendedFat}g
            </Typography>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage(
                summary?.totalFat || 0,
                recommendedFat
              )}
              sx={{ mt: 1 }}
              color="secondary"
            />
          </CardContent>
        </Card>
      </Box>

      {/* Makro Besin Dağılımı */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Makro Besin Dağılımı
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {summary && summary.totalCalories > 0 && (
            <>
              <Chip
                label={`Protein: ${(
                  (summary.totalProtein * 4 * 100) /
                  summary.totalCalories
                ).toFixed(1)}%`}
                color="success"
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                label={`Karbonhidrat: ${(
                  (summary.totalCarbohydrates * 4 * 100) /
                  summary.totalCalories
                ).toFixed(1)}%`}
                color="warning"
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                label={`Yağ: ${(
                  (summary.totalFat * 9 * 100) /
                  summary.totalCalories
                ).toFixed(1)}%`}
                color="secondary"
                sx={{ fontWeight: 'bold' }}
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Öğünler Listesi */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Öğünler</Typography>
          {canAddEditMeals && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenMealModal()}
              sx={{
                bgcolor: '#43A047',
                '&:hover': {
                  bgcolor: '#388E3C',
                },
                maxWidth: '200px',
              }}
            >
              Öğün Ekle
            </Button>
          )}
        </Box>

        {summary && summary.meals.length > 0 ? (
          <Box>
            {summary.meals.map((meal) => (
              <Card
                key={meal.id}
                sx={{
                  mb: 2,
                  borderLeft: `4px solid ${getMealTypeColor(meal.mealType)}`,
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip
                          label={meal.mealType}
                          size="small"
                          sx={{
                            backgroundColor: getMealTypeColor(meal.mealType),
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                        <Typography variant="h6">{meal.foodName}</Typography>
                      </Box>

                      {meal.quantity && meal.unit && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={1}
                        >
                          {meal.quantity} {meal.unit}
                        </Typography>
                      )}

                      <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
                        <Typography variant="body2">
                          <strong>Kalori:</strong> {meal.calories.toFixed(0)}{' '}
                          kcal
                        </Typography>
                        <Typography variant="body2">
                          <strong>Protein:</strong> {meal.protein.toFixed(1)}g
                        </Typography>
                        <Typography variant="body2">
                          <strong>Karbonhidrat:</strong>{' '}
                          {meal.carbohydrates.toFixed(1)}g
                        </Typography>
                        <Typography variant="body2">
                          <strong>Yağ:</strong> {meal.fat.toFixed(1)}g
                        </Typography>
                        {meal.fiber > 0 && (
                          <Typography variant="body2">
                            <strong>Lif:</strong> {meal.fiber.toFixed(1)}g
                          </Typography>
                        )}
                      </Box>

                      {meal.notes && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, fontStyle: 'italic' }}
                        >
                          {meal.notes}
                        </Typography>
                      )}

                      {meal.photoUrl && (
                        <Box sx={{ mt: 2 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPhotoPreview(meal.photoUrl || null);
                              setSelectedMeal(meal);
                            }}
                          >
                            <PhotoCameraIcon />
                          </IconButton>
                          <Typography variant="caption" sx={{ ml: 1 }}>
                            Fotoğraf var
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {canAddEditMeals && (
                      <Box display="flex" gap={1}>
                        {isDietitian && (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenMealModal(meal)}
                            color="primary"
                            title="Düzenle"
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMeal(meal.id)}
                          color="error"
                          title="Sil"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Box
            textAlign="center"
            py={4}
            onClick={canAddEditMeals ? () => handleOpenMealModal() : undefined}
            sx={{
              border: `2px dashed ${colors.border.light}`,
              borderRadius: 2,
              ...(canAddEditMeals
                ? {
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: colors.primary.main,
                      bgcolor: colors.alpha.primary05,
                      transform: 'translateY(-2px)',
                      boxShadow: colors.shadows.card,
                    },
                  }
                : { cursor: 'default' }),
            }}
          >
            <RestaurantIcon
              sx={{ fontSize: 48, color: colors.border.light, mb: 2 }}
            />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Henüz öğün eklenmemiş
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Öğün Ekleme/Düzenleme Modal */}
      <MealEntryModal
        open={isMealModalOpen}
        onClose={handleCloseMealModal}
        onSave={handleAddMeal}
        clientId={clientId}
        clientName={clientName}
        selectedDate={selectedDate}
        initialData={editingMeal || undefined}
      />

      {/* Fotoğraf Önizleme Dialog */}
      <Dialog
        open={!!photoPreview}
        onClose={() => {
          setPhotoPreview(null);
          setSelectedMeal(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedMeal?.foodName} - {selectedMeal?.mealType}
        </DialogTitle>
        <DialogContent>
          {photoPreview && (
            <img
              src={photoPreview}
              alt={selectedMeal?.foodName}
              style={{ width: '100%', borderRadius: '8px' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <CancelButton
            onClick={() => {
              setPhotoPreview(null);
              setSelectedMeal(null);
            }}
          >
            Kapat
          </CancelButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NutritionTracker;
